import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { BookingProvider, BookingInterceptor } from "@/lib/booking";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "google-site-verification", content: "i4HDxKbDcOaCFxEXCt6M_5KJB8oH8ASb0VXunnyRjzk" },
      { title: "Three Day Launch | Custom Websites Design" },
      { name: "description", content: "Fast, custom website design for coaches and consultants. Designed, built, and launched in just 3 days." },
      { property: "og:site_name", content: "Three Day Launch" },
      { property: "og:title", content: "Three Day Launch | Custom Websites Design" },
      { property: "og:description", content: "Fast, custom website design for coaches and consultants. Designed, built, and launched in just 3 days." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://threedaylaunch.com/" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/IaP0Kh8tsdauUjZv9UYgzTfsD1K2/social-images/social-1776883671337-in-the-modern-office-a-group-of-colleagues-of-diff-2026-03-16-04-44-37-utc.webp" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Three Day Launch | Custom Websites Design" },
      { name: "twitter:description", content: "Fast, custom website design for coaches and consultants. Designed, built, and launched in just 3 days." },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/IaP0Kh8tsdauUjZv9UYgzTfsD1K2/social-images/social-1776883671337-in-the-modern-office-a-group-of-colleagues-of-diff-2026-03-16-04-44-37-utc.webp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap",
      },
    ],
    scripts: [
      {
        children:
          '!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"24NiyppP2qjChZA7qx4v8h",debug:true});',
      },
      {
        // Defer GTM + GA until after window load so they don't block FCP/LCP.
        // dataLayer/gtag stubs are defined synchronously so any early gtag()
        // calls are queued and replayed once the real scripts load.
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-JW58B4HSCW');(function(){function load(){var ga=document.createElement('script');ga.async=true;ga.src='https://www.googletagmanager.com/gtag/js?id=G-JW58B4HSCW';document.head.appendChild(ga);(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-PZSJWKR9');}if(document.readyState==='complete'){setTimeout(load,0);}else{window.addEventListener('load',function(){setTimeout(load,0);});}})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Three Day Launch",
          url: "https://threedaylaunch.com",
          logo: "https://threedaylaunch.com/favicon.ico",
          description: "Custom (no-template) websites for small business owners, designed and launched in 3 days.",
          email: "hello@threedaylaunch.com",
          areaServed: "Worldwide",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Chicago",
            addressRegion: "IL",
            addressCountry: "US",
          },
          sameAs: [],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-PZSJWKR9"
            height={0}
            width={0}
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        <noscript>
          <div style={{ padding: "2rem", maxWidth: "800px", margin: "0 auto", fontFamily: "system-ui, sans-serif" }}>
            <h1>Three Day Launch — Custom Websites in 3 Days</h1>
            <p>
              Three Day Launch builds custom (no-template) websites for small business owners.
              Designed, built, and launched in just 3 days.
            </p>
            <p>
              We design professional websites for salons, gyms, plumbers, clinics, photographers,
              caterers, consultants, real estate agents, lawn care businesses, and many more local
              service businesses.
            </p>
            <h2>What we offer</h2>
            <ul>
              <li>100% custom website design (no templates)</li>
              <li>Live in 3 days from kickoff to launch</li>
              <li>Multi-page custom websites, most projects between $1,000 and $4,000</li>
              <li>Hosting setup and ongoing care plans starting at $25/month</li>
              <li>Mobile-responsive, SEO-friendly design</li>
            </ul>
            <h2>Get in touch</h2>
            <p>
              Email <a href="mailto:hello@threedaylaunch.com">hello@threedaylaunch.com</a> or
              visit <a href="https://threedaylaunch.com/contact">our contact page</a>.
            </p>
            <p>Based in Chicago. Serving clients worldwide.</p>
          </div>
        </noscript>
        <main>{children}</main>
        <Scripts />

      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <BookingProvider>
      <BookingInterceptor />
      <Outlet />
    </BookingProvider>
  );
}
